import moment from 'moment';
import { ChatMessageHistoryDataAction, DeleteMessageForMeAction } from '../../../../Actions/ChatHistory';
import { DELETE_MESSAGE_FOR_ME } from '../../../../Actions/Constants';
import { chatSeenPendingMsgBulk } from '../../../../Actions/SingleChatMessageActions';
import { formatUserIdToJid, isLocalUser } from '../../../../Helpers/Chat/User';
import messagesIdb from '../../../../Helpers/MessagesIdb';
import { Store } from '../../../../store';
import SDK from '../../../SDK';
import { getUserIdFromJid } from '../../../../Helpers/Utility';
import { isMessageExpiredLocally } from '../../../../Helpers/Chat/DisappearScheduler';
import { dropExpiredUnkept } from '../../../../features/chatDetail/keepMessage/dropExpiredUnkept';
import { SyncProgressAction } from '../../../../Actions/SyncProgressAction';
import recentChatIdb from '../../../../Helpers/RecentChatIdb';
import logger from '../../../../Helpers/logger';
import { CONFIG, updateLastSyncMessagesTime } from './config';

// ============================================================================
// CONSTANTS & UTILS
// ============================================================================

const API_FETCH_LIMIT = CONFIG.CHAT_HISTORY_FETCH_LIMIT;
const yieldToMain = () => new Promise(resolve => setTimeout(resolve, 0));
const GAP_TYPE = 'GAP_INDICATOR';

let syncLogs = {};

const log = (id = 'Summary', message, ...args) => {
    if (!syncLogs[id]) syncLogs[id] = [];
    syncLogs[id].push({ type: 'info', message, timestamp: moment().format('HH:mm:ss.SSS'), args });
};

const logTime = (id, label, startTime) => {
    const duration = ((performance.now() - startTime) / 1000).toFixed(2);
    if (!syncLogs[id]) syncLogs[id] = [];
    syncLogs[id].push({ type: 'time', label, duration: `${duration}s`, timestamp: moment().format('HH:mm:ss.SSS') });
    return duration;
};

/**
 * Limited concurrency async pool helper
 */
const asyncPool = async (poolLimit, array, iteratorFn) => {
    const ret = [];
    const executing = [];
    for (const item of array) {
        const p = Promise.resolve().then(() => iteratorFn(item));
        ret.push(p);

        if (poolLimit <= array.length) {
            const e = p.then(() => executing.splice(executing.indexOf(e), 1));
            executing.push(e);
            if (executing.length >= poolLimit) {
                await Promise.race(executing);
            }
        }
    }
    return Promise.all(ret);
};

// ============================================================================
// INTERNAL SYNCHRONIZATION HELPERS
// ============================================================================

/**
 * Handles the standard pipeline for a batch of messages:
 * 1. Transform/Enrich → 2. Redux History → 3. Pending Seen → 4. Persistent Store → 5. Yield
 */
const syncMessageBatch = async (batch, chatJid, options = {}) => {
    if (!batch || batch.length === 0) return;

    // The tombstone covers expired messages the server returns without disappear_at;
    // the timer check spares kept ones, even from a cache saved before the keep.
    const notTombstoned = batch.filter(msg => !isMessageExpiredLocally(msg.msgId));
    const activeBatch = await Store.dispatch(dropExpiredUnkept(notTombstoned, chatJid));
    if (activeBatch.length === 0) return;

    const messagesWithJid = activeBatch.map(msg => ({
        ...msg,
        userJid: chatJid,
    }));

    // 1. Update Redux UI History
    Store.dispatch(
        ChatMessageHistoryDataAction({
            statusCode: 200,
            data: messagesWithJid,
            userJid: chatJid,
            ...options,
        }),
    );

    // 2. Handle Pending Seen Logic (Bulk)
    const pendingSeenMsgs = messagesWithJid.filter(
        msg => !isLocalUser(msg.publisherId) && [0, 1].includes(msg.msgStatus),
    );
    if (pendingSeenMsgs.length > 0) {
        Store.dispatch(chatSeenPendingMsgBulk(pendingSeenMsgs));
    }

    // 3. Persist to IndexDB for offline support
    await messagesIdb.saveMessages(messagesWithJid);

    // 4. Yield control back to browser to prevent UI lockup
    await yieldToMain();
};

/**
 * Determines the API configuration (limit, direction, startId) for message fetching.
 */
const getFetchConfig = chat => {
    // Default: Fetch older messages (history)
    let config = {
        direction: 'up',
        msgId: '',
        limit: CONFIG.CHAT_HISTORY_LIMIT,
        strategy: 'normal',
    };

    // Triple fetch strategy for unread-heavy chats
    if (Number(chat.unreadCount || 0) > 50 && chat.unreadMessageId) {
        config = {
            direction: 'both',
            msgId: chat.unreadMessageId,
            limit: CONFIG.CHAT_HISTORY_LIMIT,
            strategy: 'triple',
        };
    }

    return config;
};

const sortByTs = arr => [...arr].sort((a, b) => Number(a.timestamp) - Number(b.timestamp));

const dedupeById = arr => {
    const map = new Map();
    arr.forEach(item => item?.msgId && map.set(item.msgId, item));
    return Array.from(map.values());
};

const buildGapIndicator = ({ chatId, lowerAnchor, upperAnchor }) => ({
    msgId: `gap_${chatId}`,
    msgType: GAP_TYPE,
    timestamp: Math.floor((Number(lowerAnchor?.timestamp || 0) + Number(upperAnchor?.timestamp || Date.now())) / 2),
    fromUserId: chatId,
    publisherId: chatId,
    msgBody: {},
    gapMeta: {
        lowerAnchorMsgId: lowerAnchor?.msgId || '',
        upperAnchorMsgId: upperAnchor?.msgId || '',
    },
});

const saveGapMeta = async (chatId, gapMeta) => {
    const current = (await recentChatIdb.getRecentChatById(chatId)) || {};
    await recentChatIdb.updateRecentChat(chatId, { ...current, messageGapMeta: gapMeta || null });
};

const getGapMeta = async chatId => {
    const current = (await recentChatIdb.getRecentChatById(chatId)) || {};
    return current.messageGapMeta || null;
};

const fetchTripleBlocks = async ({ chat, chatJid }) => {
    const firstBlockRes = await SDK.getChatMessages({
        toJid: chatJid,
        position: 'both',
        lastMessageId: chat.unreadMessageId,
        limit: CONFIG.CHAT_HISTORY_LIMIT,
        includeMsgObj: true,
        sendSeen: false,
    });
    const firstBlock = firstBlockRes?.statusCode === 200 ? firstBlockRes.data || [] : [];

    const latestRes = await SDK.getChatMessages({
        toJid: chatJid,
        position: 'up',
        lastMessageId: '',
        limit: CONFIG.CHAT_HISTORY_LIMIT,
        includeMsgObj: true,
        sendSeen: false,
    });
    const latestBlock = latestRes?.statusCode === 200 ? latestRes.data || [] : [];

    const firstSorted = sortByTs(dedupeById(firstBlock));
    const latestSorted = sortByTs(dedupeById(latestBlock));
    const hasOverlap = latestSorted.some(msg => firstSorted.some(first => first.msgId === msg.msgId));
    const merged = sortByTs(dedupeById([...firstSorted, ...latestSorted]));

    if (hasOverlap || firstSorted.length === 0 || latestSorted.length === 0) {
        return { merged, gapMeta: null };
    }

    const lowerAnchor = firstSorted[firstSorted.length - 1];
    const upperAnchor = latestSorted[0];
    const gapMeta = {
        hasGap: true,
        lowerAnchorMsgId: lowerAnchor?.msgId || '',
        upperAnchorMsgId: upperAnchor?.msgId || '',
        updatedAt: Date.now(),
    };
    return {
        merged: sortByTs([...merged, buildGapIndicator({ chatId: chat.fromUserId, lowerAnchor, upperAnchor })]),
        gapMeta,
    };
};

/**
 * Performs chunked pagination from server and synchronizes each chunk.
 */
const fetchAndSyncChunks = async (chatJid, config, options = {}) => {
    if (config.strategy === 'triple' && options.chatMeta) {
        const triple = await fetchTripleBlocks({ chat: options.chatMeta, chatJid });
        if (triple.merged.length > 0) {
            await syncMessageBatch(triple.merged, chatJid, options);
        }
        await saveGapMeta(options.chatMeta.fromUserId, triple.gapMeta);
        return triple.merged.length;
    }

    let totalFetched = 0;
    let currentFetchMsgId = config.msgId;
    const targetLimit = config.limit;

    log(chatJid, `📡 Starting fetch: ${targetLimit} messages needed`);

    while (totalFetched < targetLimit) {
        const currentBatchSize = Math.min(targetLimit - totalFetched, API_FETCH_LIMIT);
        if (totalFetched > 0) {
            log(chatJid, `    ↳ Progress: ${totalFetched}/${targetLimit} synced...`);
        }

        const response = await SDK.getChatMessages({
            toJid: chatJid,
            position: config.direction,
            lastMessageId: currentFetchMsgId,
            limit: currentBatchSize,
            includeMsgObj: true,
            sendSeen: false,
        });

        if (response.statusCode === 200 && response.data && response.data.length > 0) {
            await syncMessageBatch(response.data, chatJid, options);

            totalFetched += response.data.length;

            // Handle pagination end
            if (response.data.length < currentBatchSize) break;
            currentFetchMsgId = response.data[response.data.length - 1].msgId;
        } else {
            break;
        }
    }

    return totalFetched;
};

// ============================================================================
// CHAT PROCESSOR
// ============================================================================

/**
 * Orchestrates synchronization for a single chat
 */
export const processChat = async (chat, options = {}) => {
    const chatJid = formatUserIdToJid(chat.fromUserId, chat.chatType);
    const result = { chatJid, fetched: 0, cached: 0, error: false };
    const { forceFetch = false } = options;

    try {
        // --- Step 1: Restore Cache ---
        const cachedMessages = await messagesIdb.getMessagesByUserJid(chatJid, 200);
        if (cachedMessages && cachedMessages.length > 0) {
            result.cached = cachedMessages.length;
            log(chatJid, `📦 Found cached messages: ${cachedMessages.length}`);
            await syncMessageBatch(cachedMessages, chatJid, { ...options, chatType: chat.chatType });

            // If not forcing fetch, check if cache is fresh enough
            if (!forceFetch) {
                log(chatJid, '✨ Cache is fresh - skipping API fetch');
                const gapMeta = await getGapMeta(chat.fromUserId);
                if (!gapMeta?.hasGap) return result;
            }
        }

        // --- Step 2: Fetch Server Updates ---
        log(chatJid, `📡 Fetching messages`);
        const fetchConfig = getFetchConfig(chat);
        result.fetched = await fetchAndSyncChunks(chatJid, fetchConfig, { ...options, chatMeta: chat });

        return result;
    } catch (error) {
        if (!syncLogs[chatJid]) syncLogs[chatJid] = [];
        syncLogs[chatJid].push({ type: 'error', message: `Sync failure for chat ${chat.fromUserId}`, error });
        return { ...result, error: true };
    }
};

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Main entry point for message synchronization across multiple chats
 */
export const syncAllMessages = async (chats, options = {}) => {
    if (!chats || chats.length === 0) return;

    const { progressRange = [85, 100] } = options;
    const [startProgress, endProgress] = progressRange;

    const start = performance.now();
    syncLogs = {}; // Reset logs for a new sync session
    logger.debug('Summary', `🚀 Starting sync for ${chats.length} chats... options:`, options);

    let processedChats = 0;
    const totalChats = chats.length;

    const results = await asyncPool(CONFIG.BATCH_SIZE, chats, async chat => {
        const result = await processChat(chat, options);
        processedChats++;

        // Update progress based on chats processed
        const currentProgress = startProgress + (endProgress - startProgress) * (processedChats / totalChats);
        Store.dispatch(SyncProgressAction(Math.floor(currentProgress)));

        return result;
    });

    const stats = results.reduce(
        (acc, r) => ({
            fetched: acc.fetched + (r?.fetched || 0),
            cached: acc.cached + (r?.cached || 0),
            errors: acc.errors + (r?.error ? 1 : 0),
        }),
        { fetched: 0, cached: 0, errors: 0 },
    );

    const duration = logTime('Summary', '✅ Comprehensive message sync complete', start);
    logger.debug(
        `[MessageSync] 📊 Summary: ${results.length} chats processed | ${stats.fetched} fetched | ${stats.cached} cached | ${stats.errors} errors | ${duration}s`,
        {
            details: syncLogs,
            stats,
        },
    );
};

export const fetchOlderMessages = async ({ _chatId, chatJid, anchorId, chatType }) => {
    const cached = await messagesIdb.getMessagesByUserJid(chatJid, 3000);
    const anchor = await messagesIdb.getMessageById(anchorId);
    if (anchor?.timestamp) {
        const older = sortByTs(cached)
            .filter(msg => Number(msg.timestamp) < Number(anchor.timestamp) && msg.msgType !== GAP_TYPE)
            .slice(-CONFIG.CHAT_HISTORY_LIMIT);
        if (older.length > 0) {
            await syncMessageBatch(older, chatJid, { chatType });
            return { count: older.length, source: 'cache' };
        }
    }

    const res = await SDK.getChatMessages({
        toJid: chatJid,
        position: 'up',
        lastMessageId: anchorId,
        limit: CONFIG.CHAT_HISTORY_LIMIT,
        includeMsgObj: true,
        sendSeen: false,
    });
    const apiData = res?.statusCode === 200 ? res.data || [] : [];
    if (apiData.length > 0) await syncMessageBatch(apiData, chatJid, { chatType });
    return { count: apiData.length, source: 'api' };
};

export const fillGapMessages = async ({ chatId, chatJid, chatType }) => {
    const gapMeta = await getGapMeta(chatId);
    if (!gapMeta?.hasGap || !gapMeta.upperAnchorMsgId) return { count: 0, closed: true };

    const res = await SDK.getChatMessages({
        toJid: chatJid,
        position: 'up',
        lastMessageId: gapMeta.upperAnchorMsgId,
        limit: CONFIG.CHAT_HISTORY_LIMIT,
        includeMsgObj: true,
        sendSeen: false,
    });
    const apiData = res?.statusCode === 200 ? res.data || [] : [];
    if (apiData.length === 0) return { count: 0, closed: false };

    await syncMessageBatch(apiData, chatJid, { chatType });
    const ids = new Set(apiData.map(m => m.msgId));
    if (ids.has(gapMeta.lowerAnchorMsgId)) {
        await saveGapMeta(chatId, null);
        return { count: apiData.length, closed: true };
    }

    const sorted = sortByTs(apiData);
    await saveGapMeta(chatId, {
        ...gapMeta,
        upperAnchorMsgId: sorted[0]?.msgId || gapMeta.upperAnchorMsgId,
        updatedAt: Date.now(),
    });
    return { count: apiData.length, closed: false };
};

/**
 * Retrieves the most accurate timestamp for a given message ID from cache
 */
export const getMessageTimestamp = async msgId => {
    if (!msgId) return null;

    try {
        const message = await messagesIdb.getMessageById(msgId);
        if (!message) return null;

        // Priority 1: Numeric timestamp (highest precision)
        if (message.timestamp && typeof message.timestamp === 'number') {
            return message.timestamp;
        }

        // Priority 2: String createdAt / timestamp
        if (message.createdAt) return message.createdAt;
        if (message.timestamp) return message.timestamp;

        return null;
    } catch (error) {
        logger.error(`❌ Failed to get timestamp for msgId ${msgId}:`, error);
        return null;
    }
};

/**
 * Dedicated sync for previous messages in an active conversation
 * Iteratively fetches messages until no more data is returned by the server.
 */
export const updatePreviousMessages = async ({ toJid, lastSyncTime }) => {
    try {
        log(toJid, `🔄 Starting continuous sync from ${lastSyncTime}`);
        if (!lastSyncTime) return; // Don't sync if no starting point

        // Ensure initial lastSyncTime is 13 digits (normalize 10s or 16us to 13ms)
        const normalizeToMs = ts => {
            const len = String(ts).length;
            if (len === 10) return ts * 1000;
            if (len === 16) return Math.floor(ts / 1000);
            return ts;
        };

        let currentSyncTime = normalizeToMs(lastSyncTime);
        let totalSynced = 0;
        let batchCount = 0;
        const limit = 1000;

        log(toJid, `🔄 Starting continuous sync from ${currentSyncTime}`);

        while (true) {
            batchCount++;
            const params = { toJid, lastSyncTime: currentSyncTime, limit, currentSyncTime };
            const res = await SDK.syncChatMessage(params);
            if (res.statusCode === 200 && res?.data?.length > 0) {
                const fetchedCount = res.data.length;
                // Filter messages based on deletedStatus
                const deletedBatch = res.data.filter(msg => msg.deleteByStatus !== 0);
                const normalBatch = res.data.filter(msg => msg.deleteByStatus === 0);

                if (deletedBatch.length > 0) {
                    const deletedMsgIds = deletedBatch.map(msg => msg.msgId).filter(Boolean);
                    const fromUserId = getUserIdFromJid(toJid);
                    const deletePayload = {
                        fromUserId,
                        msgIds: deletedMsgIds,
                        chatType: deletedBatch[0]?.chatType,
                    };

                    // Bulk Delete from Redux
                    Store.dispatch(DeleteMessageForMeAction(deletePayload));
                    // Bulk Delete from IndexedDB
                    await messagesIdb.updateDeletedMessagesInIdb(DELETE_MESSAGE_FOR_ME, deletePayload);
                }

                if (normalBatch.length > 0) {
                    totalSynced += normalBatch.length;
                    // Update Redux, IDB, etc.
                    await syncMessageBatch(normalBatch, toJid);
                }
                let lastMsgTime = Date.now();

                if (lastMsgTime) {
                    currentSyncTime = normalizeToMs(lastMsgTime);
                    updateLastSyncMessagesTime(getUserIdFromJid(toJid), currentSyncTime);
                } else {
                    // Fallback to avoid infinite loop if no timestamp
                    log(toJid, '⚠️ No timestamp found in message batch, stopping sync to avoid loop');
                    break;
                }

                // If we fetched less than the limit, we've reached the end
                if (fetchedCount < limit) {
                    break;
                }
            } else {
                if (res.statusCode !== 200) {
                    log(toJid, `❌ Sync failed with status ${res.statusCode}`);
                }
                break;
            }
        }

        if (totalSynced > 0) {
            log(toJid, `✅ Continuous sync complete: ${totalSynced} messages synced across ${batchCount} batches`);
        } else {
            log(toJid, `ℹ️ No new messages to sync`);
        }
    } catch (error) {
        if (!syncLogs[toJid]) syncLogs[toJid] = [];
        syncLogs[toJid].push({ type: 'error', message: `updatePreviousMessages failed for ${toJid}`, error });
    }
};
